// ABOUTME: Public page for checking Facebook data deletion request status
// ABOUTME: Allows users to verify their deletion request without authentication

import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import api from '../services/api';

interface DeletionStatus {
  confirmationCode: string;
  status: 'Pending' | 'Completed' | 'Failed';
  statusMessage: string;
  requestedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

export default function DataDeletionStatusPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<DeletionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const code = searchParams.get('code');

  useEffect(() => {
    if (!code) return;

    const fetchStatus = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/api/data-deletion-status?code=${code}`);
        setStatus(response.data);
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status?: number } };
        if (axiosErr.response?.status === 404) {
          setError('Deletion request not found. The confirmation code may be invalid or expired.');
        } else {
          setError('An error occurred while retrieving your deletion request status. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [code]);

  if (!code) {
    return (
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" gutterBottom>Data Deletion Status</Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            Invalid or expired confirmation code. Please check the link you were given.
          </Alert>
          <Typography variant="body2">
            <Link to="/login">Return to login</Link>
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Facebook Data Deletion Status
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          BePono — Data Privacy
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {status && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              {status.status === 'Completed' && <CheckCircleIcon color="success" />}
              {status.status === 'Pending' && <HourglassEmptyIcon color="warning" />}
              {status.status === 'Failed' && <ErrorIcon color="error" />}
              <Chip
                label={status.status}
                color={
                  status.status === 'Completed' ? 'success' :
                  status.status === 'Pending' ? 'warning' : 'error'
                }
                size="small"
              />
            </Box>

            <Alert
              severity={
                status.status === 'Completed' ? 'success' :
                status.status === 'Pending' ? 'info' : 'error'
              }
              sx={{ mb: 3 }}
            >
              {status.statusMessage}
            </Alert>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Confirmation Code
            </Typography>
            <Typography variant="body1" fontFamily="monospace" fontWeight="bold" sx={{ mb: 2 }}>
              {status.confirmationCode}
            </Typography>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Requested
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {new Date(status.requestedAt).toLocaleString()}
            </Typography>

            {status.completedAt && (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(status.completedAt).toLocaleString()}
                </Typography>
              </>
            )}
          </Box>
        )}

        <Divider sx={{ mt: 3, mb: 2 }} />
        <Typography variant="body2">
          <Link to="/login">Return to BePono login</Link>
        </Typography>
      </Paper>
    </Container>
  );
}
